import type { ContainerInfo, CreateProjectInput, DockerHealth, ImportSecretsResult, Project, ProjectHealth, ProjectMember, ProjectSecretMeta, ProjectSnapshot, UpdateProjectInput } from "@codvero/shared";
import { ApiFailure, api, del, get, post } from "./client.ts";

export const projectsApi = {
  list: () => get<Project[]>("/api/projects"),
  /** The user's personal space behind the Chat section (created and started on first use). */
  personal: () => get<Project>("/api/projects/personal"),
  get: (id: string) => get<Project>(`/api/projects/${id}`),
  create: (input: CreateProjectInput) => post<Project>("/api/projects", input),
  update: (id: string, input: UpdateProjectInput) => api<Project>(`/api/projects/${id}`, { method: "PATCH", json: input }),
  remove: (id: string, purge: boolean) => del(`/api/projects/${id}?purge=${purge}`),
  container: (id: string) => get<ContainerInfo>(`/api/projects/${id}/container`),
  start: (id: string) => post<Project>(`/api/projects/${id}/start`),
  stop: (id: string) => post<Project>(`/api/projects/${id}/stop`),
  restart: (id: string) => post<Project>(`/api/projects/${id}/restart`),
  recreate: (id: string) => post<Project>(`/api/projects/${id}/recreate`),
  members: (id: string) => get<ProjectMember[]>(`/api/projects/${id}/members`),
  addMember: (id: string, email: string, role: "editor" | "viewer") => post<void>(`/api/projects/${id}/members`, { email, role }),
  removeMember: (id: string, userId: string) => del(`/api/projects/${id}/members/${userId}`),
  secrets: (id: string) => get<ProjectSecretMeta[]>(`/api/projects/${id}/secrets`),
  setSecret: (id: string, key: string, value: string) => api<void>(`/api/projects/${id}/secrets`, { method: "PUT", json: { key, value } }),
  parseEnv: async (id: string, file: File) => {
    const fd = new FormData();
    fd.set("file", file, file.name || ".env");
    const res = await fetch(`/api/projects/${id}/secrets/parse-env`, { method: "POST", body: fd, credentials: "same-origin" });
    const body = (await res.json()) as { error?: string; message?: string; entries: { key: string; value: string }[]; skipped: string[]; existing: string[] };
    if (!res.ok) throw new ApiFailure((body.error as never) ?? "internal", res.status, body.message);
    return body;
  },
  importSecrets: (id: string, entries: { key: string; value: string }[]) => post<ImportSecretsResult>(`/api/projects/${id}/secrets/import`, { entries }),
  deleteSecret: (id: string, key: string) => del(`/api/projects/${id}/secrets/${encodeURIComponent(key)}`),
  dockerHealth: () => get<DockerHealth>("/api/system/docker"),
  health: (id: string) => get<ProjectHealth>(`/api/projects/${id}/health`),
  snapshots: (id: string) => get<ProjectSnapshot[]>(`/api/projects/${id}/snapshots`),
  createSnapshot: (id: string, label: string) => post<ProjectSnapshot>(`/api/projects/${id}/snapshots`, { label }),
  restoreSnapshot: (id: string, sid: string) => post<ProjectSnapshot>(`/api/projects/${id}/snapshots/${sid}/restore`),
  deleteSnapshot: (id: string, sid: string) => del(`/api/projects/${id}/snapshots/${sid}`),
  ide: (id: string) => get<{ running: boolean; port: number; origin: string }>(`/api/projects/${id}/ide`),
  ideOpen: (id: string) => post<{ url: string; expiresAt: string; started: boolean }>(`/api/projects/${id}/ide/open`),
  ideStop: (id: string) => post<void>(`/api/projects/${id}/ide/stop`),
  snapshotDownloadUrl: (id: string, sid: string) => `/api/projects/${id}/snapshots/${sid}/download`,
};

import type { ProjectOverview } from "@codvero/shared";
export const overviewApi = { list: () => get<ProjectOverview[]>("/api/projects/overview") };
